import { useEffect, useState } from "react";
import router from "../../router/index.ts";
import { helpers } from "../../util/helpers.ts";
import i18n from "../../util/i18n.ts";
import { useViewData } from "../../util/viewManager.tsx";
import { LeagueNotFoundMessage } from "../LeagueNotFoundMessage.tsx";

const SPINNER_DELAY_MS = 300;

const VIEW_NAME_DEFAULTS: Record<string, string> = {
	dailySchedule: "Matchday",
	dashboard: "Leagues",
	draft: "Draft",
	draftHistory: "Draft History",
	draftLottery: "Draft Lottery",
	draftScouting: "Draft Scouting",
	freeAgents: "Free Agents",
	gameLog: "Game Log",
	history: "Season Summary",
	leaders: "League Leaders",
	leagueDashboard: "League Dashboard",
	liveGame: "Live Game",
	negotiationList: "Negotiations",
	newLeague: "New League",
	player: "Player",
	playerRatings: "Player Ratings",
	playerStats: "Player Stats",
	playoffs: "Playoffs",
	protectPlayers: "Protect Players",
	roster: "Roster",
	standings: "Standings",
	teamFinances: "Team Finances",
	teamStats: "Team Stats",
	trade: "Trade",
	news: "News",
};

const getCurrentPath = () =>
	window.location.pathname + window.location.search + window.location.hash;

const getViewName = (id: string | undefined) => {
	if (!id) {
		return i18n.t("loadingOverlay.page", {
			ns: "ui",
			defaultValue: "page",
		});
	}

	return i18n.t(`viewNames.${id}`, {
		ns: "ui",
		defaultValue: VIEW_NAME_DEFAULTS[id] ?? id,
	});
};

export const LeagueNotFoundRecoveryOverlay = () => (
	<div
		className="global-loading-overlay global-loading-overlay-recovery"
		data-global-loading-recovery="league-not-found"
		role="alert"
	>
		<div className="container global-loading-recovery-content">
			<LeagueNotFoundMessage />
		</div>
	</div>
);

export const GlobalLoadingOverlay = () => {
	const { actions, idLoading, loading, loadingError } = useViewData();
	const [showSpinner, setShowSpinner] = useState(false);

	useEffect(() => {
		if (!loading || loadingError) {
			setShowSpinner(false);
			return;
		}

		const timeoutID = setTimeout(() => {
			setShowSpinner(true);
		}, SPINNER_DELAY_MS);

		return () => {
			clearTimeout(timeoutID);
		};
	}, [loading, loadingError, idLoading]);

	const visible = (loading && showSpinner) || loadingError !== undefined;

	useEffect(() => {
		document.body.classList.toggle("global-loading-active", visible);
		return () => {
			document.body.classList.remove("global-loading-active");
		};
	}, [visible]);

	if (!visible) {
		return null;
	}

	if (!loadingError) {
		return (
			<div
				aria-busy="true"
				aria-label={i18n.t("common.loading", {
					ns: "ui",
					defaultValue: "Loading...",
				})}
				className="global-loading-overlay"
				data-host-state="loading"
				data-host-state-host="globalLoading"
				role="status"
			>
				<div className="spinner-border text-primary global-loading-spinner">
					<span className="visually-hidden">
						{i18n.t("common.loading", {
							ns: "ui",
							defaultValue: "Loading...",
						})}
					</span>
				</div>
			</div>
		);
	}

	if (loadingError.kind === "leagueNotFound") {
		return <LeagueNotFoundRecoveryOverlay />;
	}

	const viewName = getViewName(loadingError.id);

	return (
		<div
			className="global-loading-overlay"
			data-global-loading-error={loadingError.id ?? "unknown"}
			data-host-state="error"
			data-host-state-host="globalLoading"
			role="alert"
		>
			<div className="global-loading-error">
				<h2 className="h5 mb-3">
					{i18n.t("loadingOverlay.errorTitle", {
						ns: "ui",
						defaultValue: "Error loading {{viewName}}",
						viewName,
					})}
				</h2>
				<div className="d-flex justify-content-center gap-2">
					<button
						className="btn btn-primary btn-sm"
						data-global-loading-retry=""
						onClick={() => {
							actions.clearLoadingError();
							void router.navigate(getCurrentPath(), {
								refresh: true,
								replace: true,
							});
						}}
						type="button"
					>
						{i18n.t("common.retry", {
							ns: "ui",
							defaultValue: "Retry",
						})}
					</button>
					<button
						className="btn btn-light-bordered btn-sm"
						data-global-loading-back=""
						onClick={() => {
							actions.clearLoadingError();
							if (window.history.length > 1) {
								window.history.back();
								return;
							}
							void router.navigate(helpers.leagueUrl([]), {
								replace: true,
							});
						}}
						type="button"
					>
						{i18n.t("common.back", {
							ns: "ui",
							defaultValue: "Back",
						})}
					</button>
				</div>
			</div>
		</div>
	);
};

export default GlobalLoadingOverlay;
